import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { getVolunteers, getVolunteerLocations, getVolunteerOrganizationNames, getVolunteerById, getArLeadershipDates, getVolunteerTableRows, addGroupVolunteer, removeGroupVolunteer } from "@/features/volunteers/api/volunteers.api"
import { ADMIN } from "@/shared/constants/backend-api"
import { volunteerQueryParamsSchema, VolunteerQueryParamsType } from "../schemas/volunteer-query.schema"
import { getLocalActiveTodayQueryParams } from "../lib/local-active-today-bounds"
import { useCurrentSearchParams } from "@/shared/hooks/use-current-search-params"
import { useMemo } from "react"
import { NonHomeownerUser } from "../types/volunteers.type"
import { authFetchJson } from "@/shared/lib/auth-fetch"


const mapGroupVolunteerPermissionError = (message: string): string => {
    const normalized = message.toLowerCase()
    if (normalized.includes('cannot add volunteers to a group')) {
        return 'You do not have permission to add volunteers to this group.'
    }
    if (normalized.includes('cannot remove volunteers from a group')) {
        return 'You do not have permission to remove volunteers from this group.'
    }
    if (normalized.includes('forbidden')) {
        return 'You do not have permission to perform this action.'
    }
    return message
}

/** Default params for filter-search first page (no filters). Used for prefetch so query key matches. */
export function getDefaultVolunteerTableRowsParams(): Parameters<typeof getVolunteerTableRows>[0] {
    const parsed = volunteerQueryParamsSchema.safeParse({ page: 1, limit: 50 })
    const base = parsed.success ? parsed.data : { page: 1, limit: 50 }
    return {
        ...base,
        ...getLocalActiveTodayQueryParams(),
    } as Parameters<typeof getVolunteerTableRows>[0]
}

/**
 * Use volunteers list hook,
 * Hook call react query hook to fetch volunteers list
 * @param queryParams - Volunteer query params
 * @returns Volunteers list
 */
export const useVolunteersList = () => {
    const queryParams = useVolunteersListQueryParams()
    const query = useQuery({
        queryKey: [ADMIN.base, queryParams],
        queryFn: () => getVolunteers(queryParams),
    })
    return query
}

/**
 * Use volunteers list query params hook,
 * Hook to get query params from url
 * @returns Volunteer query params {page: number, limit: number, search: string}
 * @example
 * const { page, limit, search } = useVolunteersListQueryParams()
 */
export const useVolunteersListQueryParams = () => {
    const searchParams = useCurrentSearchParams<VolunteerQueryParamsType>([
        "page",
        "limit",
        "search",
        "locationServing",
        "roles",
        "arrivalDate",
        "departureDate",
        "organization",
        "addedFrom",
        "addedTo",
        "lastActiveAtFrom",
        "lastActiveAtTo",
        "status",
        "serveDatesRegionId",
    ])
    const parsedSearchParams = volunteerQueryParamsSchema.safeParse({
        ...searchParams,
        page: Number(searchParams.page || 1),
        limit: Number(searchParams.limit || 50),
    })
    if(!parsedSearchParams.success) {
        throw new Error("Invalid search params")
    }
    const queryParams = parsedSearchParams.data
    return queryParams
}

/**
 * Use volunteer locations hook,
 * Hook to get volunteer locations
 * @returns Volunteer locations
 */
export const useVolunteerLocations = () => {
    const query = useQuery({
        queryKey: [ADMIN.locations],
        queryFn: () => getVolunteerLocations(),
    })
    return query
}

/**
 * Use volunteer organization names hook (for Organization column filter dropdown).
 * Cached so filter-search tab doesn't refetch when switching tabs.
 */
export const useVolunteerOrganizationNames = () => {
    const query = useQuery({
        queryKey: [ADMIN.organizations],
        queryFn: () => getVolunteerOrganizationNames(),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })
    return query
}

/**
 * Use volunteer by ID hook,
 * Hook to fetch a single volunteer by their ID
 * @param volunteerId - The ID of the volunteer to fetch
 * @returns Single volunteer data
 */
export const useVolunteerById = (volunteerId: number) => {
    const query = useQuery({
        queryKey: [ADMIN.base, volunteerId],
        queryFn: () => getVolunteerById(volunteerId),
        enabled: !!volunteerId, // Only run query if volunteerId is provided
    })
    return query
}

/**
 * Use all volunteers list hook (without pagination),
 * Hook to fetch all volunteers without pagination limits,
 * useful for dropdowns that need access to the complete dataset.
 * @returns All volunteers list
 */
export const useAllVolunteersList = () => {
    const query = useQuery({
        queryKey: [ADMIN.base, 'all-volunteers'],
        queryFn: () => getVolunteers({
            page: 1,
            limit: 1000, // Set a high limit to get all volunteers
            search: '',
        }),
    })
    return query
}

/**
 * Use volunteers and team leaders hook,
 * Hook to fetch all users with Volunteer, TeamLeader, Admin, or BaseLeader roles,
 * useful for work order assignment dropdowns.
 * @returns All volunteers, team leaders, admins, and base leaders list in the expected format
 */
export const useVolunteersAndTeamLeaders = () => {
    const query = useQuery({
        queryKey: ['volunteers-and-team-leaders'],
        queryFn: async () => {
            // Fetch users with Admin (roleId=1), BaseLeader (roleId=2), TeamLeader (roleId=3), and Volunteer (roleId=4) roles
            const res = await authFetchJson<any[]>('/user/by-role?roleId=1,2,3,4')
            if (!res.success) {
                throw new Error(res.message || 'Failed to fetch volunteers and team leaders')
            }
            if (!Array.isArray(res.data)) {
                throw new Error('Failed to fetch volunteers and team leaders: response is not an array')
            }
            const data = res.data
            
            // Transform the response to match the expected volunteer data structure.
            // Volunteer-backed fields stay nested under user.volunteer.
            return {
                success: true,
                data: {
                    volunteers: data.map((user: any) => ({
                        id: user.id,
                        user: {
                            id: user.id,
                            fullname: user.fullname,
                            email: user.email,
                            role: user.role || { id: 0, name: 'Unknown' },
                            volunteer: user.volunteer
                                ? {
                                    organizationName: user.volunteer.organizationName ?? undefined,
                                    preferredLocation: user.volunteer.preferredLocation ?? undefined,
                                    availabilityStart: user.volunteer.availabilityStart ?? undefined,
                                    availabilityEnd: user.volunteer.availabilityEnd ?? undefined,
                                }
                                : undefined,
                        },
                    })),
                    total: data.length,
                    page: 1,
                    limit: data.length,
                }
            }
        },
    })
    return query
}

/**
 * Use volunteer table rows hook (filter-search tab).
 * Fetches minimal volunteer data for table display.
 * Region is cached (useRegionId uses React Query); table rows use staleTime for faster repeat loads.
 */
export const useVolunteerTableRows = (queryParams?: Record<string, unknown>) => {
    const params = useVolunteersListQueryParams()
    const localDateKey = new Date().toLocaleDateString("en-CA")
    const activeTodayQueryParams = useMemo(() => getLocalActiveTodayQueryParams(), [localDateKey])

    const finalParams = useMemo(() => {
        // Filter/Search should show all volunteers across regions.
        // We intentionally ignore route/query regionId here.
        const { regionId: _queryRegionId, ...safeQueryParams } = queryParams ?? {}
        return {
            ...params,
            ...safeQueryParams,
            ...activeTodayQueryParams,
        }
    }, [params, queryParams, activeTodayQueryParams])

    const query = useQuery({
        queryKey: [ADMIN.tableRows, finalParams],
        queryFn: () => getVolunteerTableRows(finalParams as Parameters<typeof getVolunteerTableRows>[0]),
        enabled: true,
        staleTime: 2 * 60 * 1000, // 2 min – faster when switching back to filter-search tab
        refetchOnWindowFocus: false,
    })

    return {
        ...query,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isPending: query.isPending,
    }
}

/**
 * Use volunteer table rows for a specific group (group members tab).
 */
export const useVolunteerTableRowsForGroup = (groupId: string | null) => {
    const localDateKey = new Date().toLocaleDateString("en-CA")
    const activeTodayQueryParams = useMemo(() => getLocalActiveTodayQueryParams(), [localDateKey])
    return useQuery({
        queryKey: [ADMIN.tableRows, 'group', groupId, activeTodayQueryParams.activeTodayStart, activeTodayQueryParams.clientTimeZone],
        queryFn: () =>
            getVolunteerTableRows(
                { page: 1, limit: 1000, ...activeTodayQueryParams },
                groupId ?? undefined
            ),
        enabled: !!groupId,
    })
}

export interface UseArLeadershipDatesOptions {
    /** When true, fetches all base leaders and admins across all regions (ignores regionId) */
    allRegions?: boolean
}

/**
 * Use AR leadership dates hook.
 * Fetches Admin and Base Leader users with serveDates for the AR Team calendar.
 * When allRegions is true, returns all base leaders/admins regardless of region.
 * Otherwise, filters by the given regionId.
 * Cached (staleTime) so AR Team tab loads faster when switching back.
 *
 * @param regionId - Region ID to filter by (ignored when allRegions is true)
 * @param options - allRegions: when true, fetch all regions
 * @returns AR leadership users with serveDates for calendar display
 */
export function useArLeadershipDates(
    regionId: number | undefined | null,
    options?: UseArLeadershipDatesOptions
) {
    const allRegions = options?.allRegions === true
    const localDateKey = new Date().toLocaleDateString("en-CA")
    const activeTodayQueryParams = useMemo(() => getLocalActiveTodayQueryParams(), [localDateKey])
    const query = useQuery({
        queryKey: [ADMIN.tableRows, 'ar-leadership', allRegions ? 'all' : regionId, activeTodayQueryParams.activeTodayStart, activeTodayQueryParams.clientTimeZone],
        queryFn: () =>
            getArLeadershipDates(allRegions ? undefined : regionId ?? undefined, activeTodayQueryParams),
        enabled: allRegions || (regionId != null && regionId > 0),
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    })
    return query
}

export function useAddGroupVolunteer(groupId: string | null) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (userId: number) => {
            const response = await addGroupVolunteer(groupId!, userId)
            if (!response.success) {
                throw new Error(mapGroupVolunteerPermissionError(response.message ?? 'Failed to add volunteer to group'))
            }
            return response
        },
        onSuccess: () => {
            if (groupId) qc.invalidateQueries({ queryKey: [ADMIN.tableRows] })
        },
        onError: (e: Error) => toast.error(mapGroupVolunteerPermissionError(e.message)),
    })
}

export function useRemoveGroupVolunteer(groupId: string | null) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (userId: number) => removeGroupVolunteer(groupId!, userId),
        onSuccess: () => {
            if (groupId) qc.invalidateQueries({ queryKey: [ADMIN.tableRows] })
            toast.success('Volunteer removed from group')
        },
        onError: (e: Error) => toast.error(mapGroupVolunteerPermissionError(e.message)),
    })
}